import { useEffect, useRef, useState } from 'react';

import { ReactComponent as BinSVG } from '@assets/SVGs/Colaborador/BinSVG.svg';
import { ReactComponent as ClipboardSVG } from '@assets/SVGs/Colaborador/ClipboardSVG.svg';
import { ReactComponent as DownloadSVG } from '@assets/SVGs/Colaborador/DownloadSVG.svg';
import { ReactComponent as LegajoSVG } from '@assets/SVGs/Colaborador/LegajoSVG.svg';
import { ReactComponent as UploadSVG } from '@assets/SVGs/Colaborador/UploadSVG.svg';
import { Loader } from '@shared/designSystem/Loader';
import styles from '@styles/components/ColaboradorLegajo.module.css';

import { ReactComponent as DOCIcon } from '@assets/SVGs/Colaborador/FileIcons/DOCIcon.svg';
import { ReactComponent as JPGIcon } from '@assets/SVGs/Colaborador/FileIcons/JPGIcon.svg';
import { ReactComponent as PDFIcon } from '@assets/SVGs/Colaborador/FileIcons/PDFIcon.svg';
import { ReactComponent as PNGIcon } from '@assets/SVGs/Colaborador/FileIcons/PNGIcon.svg';
import { ReactComponent as XLSIcon } from '@assets/SVGs/Colaborador/FileIcons/XLSIcon.svg';

import { Application } from '@core/Application';
import { GenerateUploadUrlRequest, LinkDocumentRequest, DeleteDocumentRequest } from '@services/UsersService';

export default function ColaboradorLegajo ({orgID, colaborador, hideTitle = false, readOnly = false}) {

    const [isDraggedOver, setIsDraggedOver] = useState(false);
    const [documents, setDocuments] = useState({})
    const [loading, setLoading] = useState(true);

    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchDocuments()
    }, [])

    const fetchDocuments = async () => {
        setLoading(true)
        try {
            const usersService = Application.shared().coreDependencies.usersService;
            const json = await usersService.getDocuments(colaborador.id, orgID);
            setDocuments(json || {})
        } catch (error) {
            console.error(error)
        }
        setLoading(false)
    }

    const handleUpload = async (file: File) => {
        setLoading(true)
        try {
            await uploadFileFlow(file, colaborador.id, orgID);

            fetchDocuments()
        } catch (error) {
            console.error('Upload error:', error);
        }
        setLoading(false)
    };

    const uploadFileFlow = async (file: File, userID: string, orgID: string) => {
        const usersService = Application.shared().coreDependencies.usersService;
        
        // Generate upload URL
        const uploadData: GenerateUploadUrlRequest = {
            originalName: file.name,
            contentType: file.type
        };
        
        const generateResponse = await usersService.generateUploadUrl(userID, uploadData);
        const { uploadURL, publicURL } = generateResponse;

        // Upload file directly to storage
        const uploadResponse = await fetch(uploadURL, {
            method: 'PUT',
            headers: {
                'Content-Type': file.type,
            },
            body: file,
            credentials: 'omit'
        });

        if (!uploadResponse.ok) {
            throw new Error('Failed to upload file');
        }

        // Link document to user
        const linkData: LinkDocumentRequest = {
            publicURL: publicURL,
            originalName: file.name,
            contentType: file.type
        };
        
        await usersService.linkDocument(userID, orgID, linkData);
    }

    const handleDragEnter = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggedOver(true);
    };

    const handleDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggedOver(false);
    }

    const handleDragOver = e => {
        e.preventDefault();
        e.stopPropagation();
      };

    const handleDrop = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggedOver(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
          const file = files[0];
          handleUpload(file)
        }
    };

    const handleDownload = async (downloadUrl, filename, mimetype) => {
        try {
            const usersService = Application.shared().coreDependencies.usersService;
            const blob = await usersService.getDownloadDocument(colaborador.id, downloadUrl);

            const downloadHref = window.URL.createObjectURL(blob); // Creates a URL for the blob object

            const a = document.createElement('a');  // Creates an anchor element
            a.href = downloadHref;
            a.download = filename; // Provide a filename here
            document.body.appendChild(a); // Append anchor to body
            a.click(); // Trigger a click on the anchor

            window.URL.revokeObjectURL(downloadHref); // Clean up the URL object
            a.remove(); // Remove the anchor from the body
        } catch (err) {
            console.error('Download error:', err);
        }
    };

    const handleDelete = async (documentID, filename) => { 
        console.log(documentID, filename)

        setLoading(true)
        try {
            const usersService = Application.shared().coreDependencies.usersService;
            const deleteData: DeleteDocumentRequest = {
                document_id: documentID,
                document_name: filename
            };

            const json = await usersService.deleteDocument(colaborador.id, orgID, deleteData);
            fetchDocuments()
            console.log('Delete successful', json);
        } catch (error) {
            console.error('Delete error:', error);
        }
        setLoading(false)

    }


    return(
        <div className={styles['content-container']}>
            {!hideTitle ? (
                <div className={styles['nomina-header']} style={{marginBottom: '46px'}}>
                    <div className={styles['svg-container']} style={{marginRight: '15px', marginLeft: '15px', marginTop: '-5px'}}>
                        <ClipboardSVG/>
                    </div>
                    <h2 className={styles['title-nomina']}>
                        Legajo
                    </h2>
                </div>
            ) : null}
            {!readOnly ? (
                <>
                    <div className={`${styles['input-box']} ${isDraggedOver || loading ? styles['drag-over'] : ''}`}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        style={{marginBottom: '1%'}}
                    >

                        {isDraggedOver || loading ?
                            loading ?
                            <Loader height="48px" width="48px" />
                            :
                            <UploadSVG style={{pointerEvents: 'none'}}/>
                        :
                        <>
                            <LegajoSVG/>
                            <p className={styles['input-box-text']}>
                                Soltá tus archivos acá o
                                <span onClick={() => fileInputRef.current.click()}> Examinar</span>
                                <input
                                    type="file"
                                    style={{ display: 'none' }}
                                    ref={fileInputRef}
                                    onChange={e => {
                                        const selectedFile = e.target.files[0];
                                        if (selectedFile) {
                                            // Pass the file directly to handleFormSubmit for processing.
                                            handleUpload(selectedFile)
                                        } else {
                                            console.error('No file selected!');
                                        }
                                    }}
                                />
                            </p>
                        </>
                        }

                    </div>
                    <p className={styles['input-box-subtext']}>
                        Formatos compatibles: .jpg, .png, .pdf, .doc, .xls
                    </p>
                </>
            ) : null}
            <div className={styles['documents-container']}>
                {Object.keys(documents).length === 0 ? <p className={styles['documents-placeholder']}>No hay documentos cargados aún.</p> 
                :
                Object.keys(documents).map((documentID, index) => {
                    const displayMimetypeSVG = (mimetype) => {
                        if (mimetype.includes('png')) {
                            return <PNGIcon/>
                        }
                        if (mimetype.includes('jpeg')) {
                            return <JPGIcon/>
                        }
                        if (mimetype.includes('pdf')) {
                            return <PDFIcon/>
                        }
                        if (mimetype.includes('doc')) {
                            return <DOCIcon/>
                        }
                        if (mimetype.includes('xls')) {
                            return <XLSIcon/>
                        }       
                    }

                    let document = documents[documentID]
                    return(
                        <div className={styles['document-box']} style={index % 2 === 0 ? {background: '#FBFBFB'} : {background: '#FFF'}} key={documentID}>
                            <div className={styles['mime-container']} style={{display: 'flex', flexDirection: 'row'}}>
                                <div className={styles['svg-container']} style={{marginLeft: '5px', marginRight: '15px'}}>
                                    {displayMimetypeSVG(document.mimetype)}
                                </div>
                                <p>{document.filename}</p>
                            </div>
                            <div className={styles['buttons-container']}>
                                <div className={styles['svg-container']} onClick={() => handleDownload(document.documentURL, document.filename, document.mimetype)}>
                                    <DownloadSVG/>
                                </div>
                                {!readOnly ? (
                                    <div className={styles['svg-container']} onClick={() => handleDelete(documentID, document.filename)}>
                                        <BinSVG/>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    )
                })
                }
                
            </div>
        </div>
    )
}
